import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const articleStructuredData = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Co to jest Kakobuy Spreadsheet – Kompletny Poradnik 2026",
  "description": "Dowiedz się wszystkiego o Kakobuy Spreadsheet – największej bazie produktów dla polskich kupujących. Kompletny poradnik obejmujący funkcje, bezpieczeństwo i porównania.",
  "author": {
    "@type": "Organization",
    "name": "KakobuyFinds",
    "url": "https://kakobuyfinds.cyou"
  },
  "publisher": {
    "@type": "Organization",
    "name": "KakobuyFinds",
    "url": "https://kakobuyfinds.cyou"
  },
  "datePublished": "2026-07-08",
  "dateModified": "2026-07-08",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "https://kakobuyfinds.cyou/artykul/kakobuy-spreadsheet-poradnik"
  },
  "inLanguage": "pl-PL"
};

export default function ArticlePage() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(articleStructuredData);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="py-12 md:py-16 px-6 md:px-10 lg:px-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-foreground-500 hover:text-primary-600 text-xs font-medium transition-colors mb-6"
              >
                <i className="ri-arrow-left-line"></i>
                Strona główna
              </Link>

              <div className="flex items-center gap-3 mb-3">
                <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  Poradnik
                </span>
                <span className="text-foreground-500 text-xs">8 lipca 2026</span>
                <span className="text-foreground-500 text-xs">12 min czytania</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight tracking-tight">
                Co to jest Kakobuy Spreadsheet – Kompletny Poradnik dla Polskich Kupujących 2026
              </h1>

              <p className="text-foreground-600 text-base mt-4 leading-relaxed">
                Wszystko, co musisz wiedzieć o <strong>Kakobuy Spreadsheet</strong> – od podstawowych funkcji 
                po zaawansowane strategie wyszukiwania najlepszych okazji zakupowych na chińskich platformach.
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Thoughtful%20flat%20lay%20of%20a%20paper%20spreadsheet%20printout%20with%20handwritten%20notes%20surrounded%20by%20coffee%20cup%20and%20reading%20glasses%20on%20warm%20wooden%20desk%2C%20morning%20sunlight%2C%20editorial%20study%20vibe%2C%20terracotta%20mug%20and%20olive%20green%20notebook%20accents%2C%20intellectual%20organized%20aesthetic&width=1200&height=600&seq=article-main-guide-v2&orientation=landscape"
                alt="Kakobuy Spreadsheet poradnik"
                className="w-full h-64 md:h-80 object-cover object-top"
              />
            </div>

            <div className="prose-custom space-y-8">
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                  Czym Dokładnie Jest Kakobuy Spreadsheet?
                </h2>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  <strong>Kakobuy Spreadsheet</strong> to zaawansowana, uporządkowana baza produktów stworzona 
                  specjalnie z myślą o polskich kupujących, którzy chcą bezpiecznie i efektywnie robić zakupy 
                  na chińskich platformach marketplace. W przeciwieństwie do chaotycznego przeszukiwania 
                  setek stron produktowych, Kakobuy Spreadsheet oferuje jedno scentralizowane miejsce, 
                  gdzie znajdziesz zweryfikowane linki do produktów, szczegółowe recenzje, porównania cen 
                  oraz informacje o jakości wykonania.
                </p>
                <p className="text-foreground-700 text-sm leading-relaxed mt-3">
                  Arkusz powstał jako odpowiedź na rosnące zapotrzebowanie polskiej społeczności na 
                  rzetelne i aktualne informacje o produktach dostępnych na platformach takich jak 
                  Kakobuy, Weidian, Taobao czy 1688. Dzięki pracy zespołu doświadczonych researcherów, 
                  <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">Kakobuy Spreadsheet</Link> jest 
                  regularnie aktualizowany, co zapewnia dostęp do najświeższych znalezisk i trendów.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                  Główne Funkcje Kakobuy Spreadsheet
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                      <i className="ri-search-eye-line text-primary-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">
                      Inteligentne Wyszukiwanie
                    </h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">
                      Zaawansowane filtry kategorii pozwalają błyskawicznie znaleźć interesujące Cię produkty 
                      – od sneakersów po elektronikę.
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                      <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">
                      Weryfikacja Linków
                    </h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">
                      Każdy link przechodzi proces weryfikacji – sprawdzamy autentyczność produktu, 
                      reputację sprzedawcy i jakość wykonania.
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mb-3">
                      <i className="ri-scales-3-line text-secondary-700 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">
                      Porównania Produktów
                    </h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">
                      Porównuj ceny, jakość i opinie różnych wariantów tego samego produktu 
                      w jednym miejscu, oszczędzając czas i pieniądze.
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                      <i className="ri-refresh-line text-primary-600 text-lg"></i>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">
                      Cotygodniowe Aktualizacje
                    </h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">
                      Arkusz jest aktualizowany co tydzień – dodajemy nowe produkty, usuwamy nieaktualne 
                      linki i odświeżamy informacje o cenach.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                  Jak Zacząć Korzystać z Kakobuy Spreadsheet?
                </h2>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  Rozpoczęcie przygody z <strong>Kakobuy Spreadsheet</strong> jest niezwykle proste i nie wymaga 
                  żadnej specjalistycznej wiedzy technicznej. Poniżej przedstawiamy krok po kroku, 
                  jak zacząć korzystać z arkusza i maksymalnie wykorzystać jego potencjał.
                </p>
                
                <div className="mt-5 space-y-4">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                      1
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                        Wybierz interesującą Cię kategorię
                      </h4>
                      <p className="text-foreground-600 text-xs leading-relaxed">
                        Na stronie głównej <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">KakobuyFinds</Link> znajdziesz 
                        rozwijane menu z 14 kategoriami produktów – od Shoes, przez Hoodies/Sweaters, 
                        aż po Perfumes i Other Stuff. Wybierz kategorię, która Cię interesuje.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                      2
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                        Przeglądaj zweryfikowane produkty
                      </h4>
                      <p className="text-foreground-600 text-xs leading-relaxed">
                        Po wybraniu kategorii zostaniesz przekierowany do odpowiedniej sekcji arkusza 
                        na <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">kaspreadsheet.com</a>, 
                        gdzie znajdziesz listę starannie wyselekcjonowanych produktów.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                      3
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                        Sprawdź recenzje i oceny
                      </h4>
                      <p className="text-foreground-700 text-xs leading-relaxed">
                        Każdy produkt w arkuszu posiada ocenę jakości oraz uwagi od społeczności. 
                        Przeczytaj je uważnie przed podjęciem decyzji o zakupie.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                      4
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                        Dokonaj bezpiecznego zakupu
                      </h4>
                      <p className="text-foreground-600 text-xs leading-relaxed">
                        Kliknij w link produktu i dokonaj zakupu bezpośrednio na platformie. Zawsze 
                        korzystaj z bezpiecznych metod płatności i sprawdzaj politykę zwrotów sprzedawcy.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                  Kakobuy Spreadsheet vs Inne Platformy – Porównanie
                </h2>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  Na rynku dostępnych jest kilka alternatywnych arkuszy zakupowych, ale 
                  <strong> Kakobuy Spreadsheet</strong> wyróżnia się na tle konkurencji pod wieloma 
                  istotnymi względami. Poniżej przedstawiamy szczegółowe porównanie.
                </p>
                
                <div className="mt-5 overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 px-3 font-heading text-sm font-semibold text-foreground-900">Funkcja</th>
                        <th className="text-left py-3 px-3 font-heading text-sm font-semibold text-primary-600">Kakobuy</th>
                        <th className="text-left py-3 px-3 font-heading text-sm font-semibold text-foreground-700">CSSBuy</th>
                        <th className="text-left py-3 px-3 font-heading text-sm font-semibold text-foreground-700">Superbuy</th>
                        <th className="text-left py-3 px-3 font-heading text-sm font-semibold text-foreground-700">Oopbuy</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-3 text-foreground-700">Liczba produktów</td>
                        <td className="py-3 px-3 text-primary-600 font-semibold">1000+</td>
                        <td className="py-3 px-3 text-foreground-500">700+</td>
                        <td className="py-3 px-3 text-foreground-500">800+</td>
                        <td className="py-3 px-3 text-foreground-500">400+</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-3 text-foreground-700">Aktualizacje</td>
                        <td className="py-3 px-3 text-primary-600 font-semibold">Co tydzień</td>
                        <td className="py-3 px-3 text-foreground-500">Co 2 tygodnie</td>
                        <td className="py-3 px-3 text-foreground-500">Co tydzień</td>
                        <td className="py-3 px-3 text-foreground-500">Co miesiąc</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-3 text-foreground-700">Polska społeczność</td>
                        <td className="py-3 px-3 text-primary-600 font-semibold">Tak</td>
                        <td className="py-3 px-3 text-foreground-500">Nie</td>
                        <td className="py-3 px-3 text-foreground-500">Częściowo</td>
                        <td className="py-3 px-3 text-foreground-500">Nie</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-3 text-foreground-700">Weryfikacja linków</td>
                        <td className="py-3 px-3 text-primary-600 font-semibold">Pełna</td>
                        <td className="py-3 px-3 text-foreground-500">Podstawowa</td>
                        <td className="py-3 px-3 text-foreground-500">Częściowa</td>
                        <td className="py-3 px-3 text-foreground-500">Brak</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-3 text-foreground-700">Dostęp</td>
                        <td className="py-3 px-3 text-primary-600 font-semibold">Bezpłatny</td>
                        <td className="py-3 px-3 text-foreground-500">Bezpłatny</td>
                        <td className="py-3 px-3 text-foreground-500">Częściowo płatny</td>
                        <td className="py-3 px-3 text-foreground-500">Bezpłatny</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                  Bezpieczeństwo Podczas Korzystania z Kakobuy Spreadsheet
                </h2>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  Bezpieczeństwo jest absolutnym priorytetem dla zespołu 
                  <strong> Kakobuy Spreadsheet</strong>. Poniżej przedstawiamy kluczowe zasady, 
                  których przestrzegamy, aby zapewnić bezpieczne doświadczenia zakupowe.
                </p>
                
                <div className="mt-5 bg-background-100 rounded-xl p-6 border border-background-200/60">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900">Weryfikacja sprzedawców</h4>
                      <p className="text-foreground-600 text-xs mt-1 leading-relaxed">
                        Każdy sprzedawca, którego produkty trafiają do arkusza, przechodzi proces weryfikacji. 
                        Sprawdzamy historię transakcji, opinie kupujących oraz ogólną reputację na platformie. 
                        Sprzedawcy z niskimi ocenami lub podejrzaną historią nie są uwzględniani w naszym arkuszu.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-lock-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900">Ochrona danych</h4>
                      <p className="text-foreground-600 text-xs mt-1 leading-relaxed">
                        Nigdy nie prosimy o podanie wrażliwych danych osobowych poza oficjalnymi platformami 
                        zakupowymi. Zalecamy korzystanie z bezpiecznych metod płatności oferowanych przez 
                        platformy oraz unikanie transakcji poza ich ekosystemem.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900">Sygnalizowanie problemów</h4>
                      <p className="text-foreground-600 text-xs mt-1 leading-relaxed">
                        Jeśli napotkasz jakikolwiek problem z produktem lub sprzedawcą, zachęcamy do 
                        zgłaszania tego przez nasz formularz kontaktowy na <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">stronie głównej</Link>. 
                        Każde zgłoszenie jest przez nas analizowane, a potencjalnie niebezpieczne linki 
                        są natychmiast usuwane z arkusza.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                  Dlaczego Polscy Kupujący Wybierają Kakobuy Spreadsheet?
                </h2>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  Popularność <strong>Kakobuy Spreadsheet</strong> wśród polskich kupujących rośnie z dnia 
                  na dzień. Oto kluczowe powody, dla których coraz więcej osób wybiera właśnie ten arkusz 
                  jako swoje główne narzędzie zakupowe.
                </p>
                
                <ul className="mt-4 space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                    <div>
                      <strong className="text-foreground-900 text-sm">Największa polska społeczność</strong>
                      <p className="text-foreground-600 text-xs mt-0.5 leading-relaxed">
                        Jako jedyny arkusz z aktywną polską społecznością, Kakobuy Spreadsheet oferuje 
                        wsparcie w języku polskim i rekomendacje dostosowane do potrzeb polskich kupujących.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                    <div>
                      <strong className="text-foreground-900 text-sm">Regularne aktualizacje</strong>
                      <p className="text-foreground-600 text-xs mt-0.5 leading-relaxed">
                        Cotygodniowe aktualizacje gwarantują, że zawsze masz dostęp do najświeższych 
                        znalezisk i aktualnych cen.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                    <div>
                      <strong className="text-foreground-900 text-sm">Całkowicie bezpłatny dostęp</strong>
                      <p className="text-foreground-600 text-xs mt-0.5 leading-relaxed">
                        W przeciwieństwie do niektórych konkurencyjnych platform, Kakobuy Spreadsheet 
                        oferuje pełen dostęp do wszystkich funkcji całkowicie za darmo.
                      </p>
                    </div>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                  Najczęściej Zadawane Pytania
                </h2>
                
                <div className="space-y-3">
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                      Czy Kakobuy Spreadsheet jest legalny?
                    </h4>
                    <p className="text-foreground-600 text-xs leading-relaxed">
                      Tak, Kakobuy Spreadsheet jest w 100% legalnym narzędziem. Arkusz jedynie agreguje 
                      i organizuje publicznie dostępne linki do produktów na chińskich platformach 
                      marketplace. Nie pośredniczymy w transakcjach – wszystkie zakupy odbywają się 
                      bezpośrednio na oficjalnych platformach.
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                      Jak mogę zasugerować produkt do dodania?
                    </h4>
                    <p className="text-foreground-600 text-xs leading-relaxed">
                      Jeśli znalazłeś interesujący produkt, który Twoim zdaniem powinien znaleźć się 
                      w arkuszu, możesz skontaktować się z nami przez newsletter na 
                      <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium"> stronie głównej</Link>. 
                      Każda sugestia jest przez nas weryfikowana przed dodaniem.
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                      Czy potrzebuję konta, aby korzystać z arkusza?
                    </h4>
                    <p className="text-foreground-600 text-xs leading-relaxed">
                      Nie, Kakobuy Spreadsheet jest dostępny bez konieczności zakładania konta. 
                      Wystarczy odwiedzić naszą <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">stronę główną</Link>, 
                      wybrać kategorię i rozpocząć przeglądanie produktów.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                  Podsumowanie
                </h2>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  <strong>Kakobuy Spreadsheet</strong> to niezastąpione narzędzie dla każdego polskiego 
                  kupującego, który chce bezpiecznie i efektywnie robić zakupy na chińskich platformach. 
                  Z największą bazą zweryfikowanych produktów, regularnymi aktualizacjami i aktywną 
                  polską społecznością, nasz arkusz jest najlepszym wyborem dla osób poszukujących 
                  jakościowych produktów w atrakcyjnych cenach.
                </p>
                <p className="text-foreground-700 text-sm leading-relaxed mt-3">
                  Niezależnie od tego, czy jesteś doświadczonym kupującym, czy dopiero zaczynasz swoją 
                  przygodę z chińskimi marketplace, <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">KakobuyFinds</Link> i 
                  <strong> Kakobuy Spreadsheet</strong> zapewnią Ci wszystkie niezbędne narzędzia i informacje, 
                  aby Twoje zakupy były udane, bezpieczne i satysfakcjonujące.
                </p>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap"
                >
                  Powrót na stronę główną
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Otwórz kakobuy sheet
                </a>
              </div>
              <div className="flex items-center gap-2 text-foreground-500 text-xs">
                <span>Udostępnij:</span>
                <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line"></i>
                </button>
                <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-facebook-line"></i>
                </button>
                <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}